/**
 * Backstop for mutations whose caller handles no errors (#341 Case F).
 *
 * With `retry: 0` and no global handler, a mutation that failed and whose
 * call site forgot `onError` produced NOTHING — the write silently didn't
 * happen and the UI kept showing the optimistic result. This fires only when
 * the mutation declared no `onError` of its own, so every call site that DOES
 * handle its errors is untouched and nobody gets two messages for one
 * failure.
 *
 * Dispatched as a DOM event rather than toasted here because the QueryClient
 * is constructed outside the React tree (main.tsx) and has no access to
 * ToastProvider. `AppLayout` (App.tsx) listens and renders it — same shape as
 * `papan:online-recovery` in main.tsx.
 *
 * Pulled out of main.tsx into its own module (rather than built inline)
 * purely so it can be exercised against a real QueryClient + MutationCache in
 * a unit test — main.tsx has top-level side effects (mounts the root DOM
 * node, kicks off network requests) that make it unsafe to import directly.
 */
import { MutationCache } from '@tanstack/react-query'
import { isSessionEndedError } from '@papan/api-client'

export const UNHANDLED_MUTATION_ERROR_EVENT = 'papan:unhandled-mutation-error'

export function createMutationCache(): MutationCache {
  return new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      // Footgun: this keys off the mutation's DECLARED `onError` option. A call
      // site that instead passes an error handler only via `mutate(vars, { onError })`
      // would leave `mutation.options.onError` unset and get a double toast. No
      // such call sites exist today — keep handling errors on the mutation itself.
      if (mutation.options.onError) return
      // #1316: an answer dropped because its session ended — nothing failed for the person now here
      if (isSessionEndedError(error)) return
      // A 429 is already surfaced (localized, with the wait hint) by the App's
      // dedicated onApiFailure rate-limit listener (#341 Case D). Skipping it
      // here avoids a SECOND, un-localized toast for a mutation that declared no
      // onError of its own — a double message in the exact "one clear message"
      // domain this backstop is meant to protect.
      if (error instanceof Error && (error as { status?: number }).status === 429) return
      // A non-Error rejection carries no message worth showing, so dispatch
      // `undefined` and let the App-side listener supply its i18n'd generic
      // fallback rather than this layer's hardcoded English. (Error.message
      // itself is inherently un-i18n'd — that's server/runtime text.)
      const message = error instanceof Error ? error.message : undefined
      window.dispatchEvent(
        new CustomEvent(UNHANDLED_MUTATION_ERROR_EVENT, { detail: { message } }),
      )
    },
  })
}
